import { defineAsyncComponent } from 'vue';

import type { MediaFilters, TikTokMediaType } from '@/apis/queries/media-list.types';
import type {
  CompetitiveOverviewConfig,
  CompetitiveBenchmarkQueryInput,
  CompetitivePostsScopedFilters,
  MostEngagingPostsFilterInput,
  MostEngagingPostsSeeMoreInput,
} from '@/app/competitive/useCompetitiveChannelConfig';

import { COMPETITOR_PLATFORM_OPTIONS } from '@/app/settings/constants';
import MediaCard from '@/components/dashingUI/MediaCard.vue';
import { LOGICAL_OPERATORS } from '@/config';
import { EVENT } from '@/stores/socket';
import { toUtcQueryDateTime } from '@/utils';

export const TIKTOK_FILTERS = Object.freeze({
  FILTER_POST_TYPES: 'competitive_post_types',
});

export const TIKTOK_COMPETITOR_POSTS_ROUTE_NAME = 'competitors.tiktok.competitorPosts';

export const COMPETITOR_RESOLUTION_MAX_WAIT_MS = 120_000;

export const TIKTOK_MEDIA_TYPES = Object.freeze([
  { label: 'Photo', value: 'photo' },
  { label: 'Video', value: 'video' },
]);

export const TIKTOK_POST_METRICS = Object.freeze([
  {
    value: 'AVG_ENGAGEMENT_RATE_VIEWS',
    label: 'Avg. Engagement Rate (Views)',
    tooltip:
      'The average percentage of people who engaged with your posts after seeing them, calculated using video views.',
    percent: true,
    default: true,
  },
  {
    value: 'AVG_ENGAGEMENT_RATE_FOLLOWERS',
    label: 'Avg. Engagement Rate (Followers)',
    tooltip:
      'The average percentage of people who engaged with your posts, calculated using followers.',
    percent: true,
  },
  {
    value: 'TOTAL_ENGAGEMENTS',
    label: 'Engagements',
    tooltip:
      'The total number of engagements your posts received. Engagements = Likes + Comments + Shares + Favorites.',
  },
  {
    value: 'VIDEO_VIEWS',
    label: 'Video Views',
    tooltip: 'The number of times your videos were viewed.',
  },
  {
    value: 'AVG_VIDEO_VIEWS',
    label: 'Avg. Video Views',
    tooltip: 'The average number of times your videos were viewed.',
  },
  {
    value: 'NUMBER_OF_POSTS',
    label: 'Number of Posts',
    tooltip: 'The number of posts published during the selected period.',
  },
  { value: 'LIKES', label: 'Likes', tooltip: 'The number of likes your posts received.' },
  { value: 'COMMENTS', label: 'Comments', tooltip: 'The number of comments your posts received.' },
  { value: 'SHARES', label: 'Shares', tooltip: 'The number of times your posts were shared.' },
  {
    value: 'FAVORITES',
    label: 'Favorites / Saves',
    tooltip: 'The total number of times your posts were added to favorites.',
  },
]);

export const TIKTOK_ACCOUNT_METRICS = Object.freeze([
  {
    value: 'TOTAL_FOLLOWERS',
    label: 'Followers - Total',
    tooltip: 'The total number of followers on your account.',
  },
  {
    value: 'NET_NEW_FOLLOWERS',
    label: 'Followers - Net New',
    tooltip: 'The net new number of followers your account gained.',
  },
  {
    value: 'FOLLOWER_GROWTH_RATE',
    label: 'Followers - Growth Rate',
    tooltip: 'The percentage increase in followers during the selected period.',
    percent: true,
  },
  {
    value: 'LIKES_GROWTH_RATE',
    label: 'Likes - Growth Rate (Account)',
    tooltip: 'The percentage increase in account likes during the selected period.',
    percent: true,
  },
  {
    value: 'NET_NEW_LIKES',
    label: 'Likes - Net New (Account)',
    tooltip: 'The net new number of likes your account gained during the selected period.',
  },
]);

export const TIKTOK_METRICS = Object.freeze([...TIKTOK_POST_METRICS, ...TIKTOK_ACCOUNT_METRICS]);

export const TIKTOK_POST_SORT_OPTIONS = Object.freeze([
  { value: 'DATE', label: 'Recently Added' },
  { value: 'TIKTOK_LIKES', label: 'Likes', field: 'likes' },
  { value: 'TIKTOK_COMMENTS', label: 'Comments', field: 'comments' },
  { value: 'TIKTOK_SHARES', label: 'Shares', field: 'shares' },
  { value: 'TIKTOK_FAVORITES', label: 'Favorites / Saves', field: 'favorites' },
  { value: 'TIKTOK_VIEWS', label: 'Views', field: 'views' },
  {
    value: 'TIKTOK_VIEWS_BASED_ENGAGEMENT_RATE',
    label: 'Engagement Rate (Views)',
    field: 'viewsBasedEngagementRate',
  },
  {
    value: 'TIKTOK_FOLLOWERS_BASED_ENGAGEMENT_RATE',
    label: 'Engagement Rate (Followers)',
    field: 'followersBasedEngagementRate',
  },
]);

export const TIKTOK_TOP_PERFORMING_MEDIA_METRICS = Object.freeze({
  labels: {
    viewsBasedEngagementRate: 'Engagement Rate (Views)',
    likes: 'Likes',
    comments: 'Comments',
    shares: 'Shares',
  },
  metrics: ['viewsBasedEngagementRate', 'likes', 'comments', 'shares'],
});

// TODO[SC-210798]: Wire Overview defaults into shared benchmark graph filter state.
export const TIKTOK_FILTER_DEFAULTS = Object.freeze({
  overview: {
    reportingPeriod: { preset: 'LAST_7_DAYS' },
    competitors: [],
  },
  posts: {
    reportingPeriod: { preset: 'ALL_TIME' },
    captionContain: [],
    captionNotContain: [],
    competitors: [],
    hashtags: [],
    mediaType: [],
    contentTags: [],
  },
});

const ADD_COMPETITOR_NOTIFICATIONS = Object.freeze({
  pendingDelay: 8000,
  pendingMessage: 'Adding competitors',
  pendingSubtext:
    "We're pulling in data for these accounts. This can take up to 2 minutes — feel free to keep working while we finish up.",
  requestError: "We couldn't add these competitors. Try again.",
  refreshError:
    "Competitors were added, but the page couldn't refresh. Refresh the page to see the latest data.",
  successDelay: 8000,
});

export const TIKTOK_ADD_COMPETITOR_CONFIG = Object.freeze({
  atCapacityMessage: COMPETITOR_PLATFORM_OPTIONS.TIKTOK.limitReachedMessage,
  infoMessage:
    'Double-check each handle before adding. It may take up to 2 minutes to validate due to API limitations.',
  maxBulkHandles: 50,
  notifications: ADD_COMPETITOR_NOTIFICATIONS,
  placeholder: 'Enter full TikTok handle',
});

export const TIKTOK_CUSTOMER_COPY = Object.freeze({
  platformName: 'TikTok',
  pendingCompetitorTooltip:
    "We're still resolving this handle. It can take up to 2 minutes to verify.",
  inaccessibleCompetitorTooltip:
    "This TikTok Competitor's handle has changed, is private, or is no longer valid. Remove this competitor and try a different handle.",
  noBrandPermission: "Looks like this feature isn't included in your plan!",
  noUserPermission: "Looks like you don't have permission to use this feature!",
  noMediaForDatesEmptyMessages: 'No posts found during this time period',
  noMediaForFiltersEmptyMessages: 'No posts match your selected filters',
  mostEngagingPostsTooltip:
    'Highest performing TikTok competitor posts published during the selected date range by engagement rate',
  mostEngagingPostsEmptyMessage: 'No Competitor Posts',
  mostEngagingPostsReportTitle: 'Top Performing Posts',
  shareofVoice:
    'Share is the measure of your specific brand metric divided by the total of the competitors selected',
  noCompetitorsEmptyStatus: {
    title: 'Add Competitors to Get Started',
  },
  noFilteredCompetitorsEmptyStatus: {
    message: 'There are no matching competitors for this set of filters. Remove some to try again.',
    title: 'Remove Filters',
  },
});

export const COMPETITIVE_OVERVIEW_CONFIG = Object.freeze({
  filter: {
    default: TIKTOK_FILTER_DEFAULTS.overview,
    queryParamMapping: {
      'reportingPeriod.range.start': { queryParamName: 'start_date', type: 'date' },
      'reportingPeriod.range.end': { queryParamName: 'end_date', type: 'date' },
      competitors: { queryParamName: 'competitors', type: 'array', elementType: 'string' },
    },
  },
  benchmark: {
    query: {
      build: ({
        brandId,
        endDate,
        metric,
        sourceAccountIds,
        startDate,
      }: CompetitiveBenchmarkQueryInput) => {
        const start = toUtcQueryDateTime(startDate);
        const end = toUtcQueryDateTime(endDate);
        return {
          brandIds: [brandId],
          competitorSourceAccountIds: [...new Set(sourceAccountIds)].sort(),
          channels: 'TIKTOK_COMPETITIVE',
          metrics: metric,
          startDate: start,
          endDate: end,
          contextStartDate: start,
          contextEndDate: end,
          reportType: 'BAR_METRIC',
          aggregateBy: 'BRAND',
          includeShareOfVoice: true,
        };
      },
    },
    channelKey: 'TIKTOK_COMPETITIVE',
    inaccessibleTooltip: TIKTOK_CUSTOMER_COPY.inaccessibleCompetitorTooltip,
    metrics: TIKTOK_METRICS,
    pendingTooltip: TIKTOK_CUSTOMER_COPY.pendingCompetitorTooltip,
  },
  mostEngagingPosts: {
    card: {
      component: MediaCard,
      props: {
        displayMetrics: TIKTOK_TOP_PERFORMING_MEDIA_METRICS.metrics,
        metricLabels: TIKTOK_TOP_PERFORMING_MEDIA_METRICS.labels,
        hideSentiment: true,
        showUserDetails: true,
      },
    },
    copy: {
      emptyMessage: TIKTOK_CUSTOMER_COPY.mostEngagingPostsEmptyMessage,
      reportTitle: TIKTOK_CUSTOMER_COPY.mostEngagingPostsReportTitle,
      tooltip: TIKTOK_CUSTOMER_COPY.mostEngagingPostsTooltip,
    },
    seeMore: {
      buildRoute: ({
        endDate,
        selectedSourceAccountIds,
        startDate,
      }: MostEngagingPostsSeeMoreInput) => ({
        name: TIKTOK_COMPETITOR_POSTS_ROUTE_NAME,
        query: {
          ...(selectedSourceAccountIds.length
            ? { competitors: [...new Set(selectedSourceAccountIds)].sort() }
            : {}),
          endDate,
          sort: '-TIKTOK_VIEWS_BASED_ENGAGEMENT_RATE',
          startDate,
        },
      }),
      label: 'See More',
    },
    query: {
      buildFilters: ({ endDate, sourceAccountIds, startDate }: MostEngagingPostsFilterInput) => ({
        brandMediaTypes: ['TIKTOK_OTHER'],
        sourceAccountIds: [...new Set(sourceAccountIds)].sort(),
        sourceCreatedAt: { start: startDate, end: endDate },
      }),
      limit: 5,
      sort: '-TIKTOK_ENGAGEMENT_RATE',
    },
  },
  handleResolutionError: {
    component: defineAsyncComponent(
      () => import('@/app/competitive/components/CompetitorResolutionBanner.vue'),
    ),
    props: {
      maxWaitMs: COMPETITOR_RESOLUTION_MAX_WAIT_MS,
      socketEvent: EVENT.COMPETITOR_RESOLUTION_UPDATED,
      source: COMPETITOR_PLATFORM_OPTIONS.TIKTOK.value,
      sourceLabel: TIKTOK_CUSTOMER_COPY.platformName,
    },
  },
}) satisfies CompetitiveOverviewConfig;

function isTikTokMediaType(value: string): value is TikTokMediaType {
  return value === 'PHOTO' || value === 'VIDEO';
}

function buildContentTagFilters({
  contentTagMatchType,
  contentTags = [],
}: CompetitivePostsScopedFilters): Partial<MediaFilters> {
  const selectedContentTags = contentTags.filter(Boolean);
  if (selectedContentTags.length === 0) return {};

  return {
    contentTags: selectedContentTags,
    contentTagMatchType: contentTagMatchType === 'ALL' ? 'ALL' : 'ANY',
  };
}

function buildHashtagFilters({ hashtags = [] }: CompetitivePostsScopedFilters) {
  const indications = hashtags.flatMap((value) => {
    const hashtag = value.replace(/^#+/, '');
    return hashtag ? [`#${hashtag}`] : [];
  });
  if (indications.length === 0) return {};

  return { indicationFilter: { indications, operator: LOGICAL_OPERATORS.AND } };
}

function buildCaptionKeywords({
  captionContain = [],
  captionNotContain = [],
}: CompetitivePostsScopedFilters) {
  const contain = captionContain.filter(Boolean);
  const notContain = captionNotContain.filter(Boolean);
  if (contain.length === 0 && notContain.length === 0) return undefined;

  return {
    ...(contain.length > 0 ? { contain } : {}),
    ...(notContain.length > 0 ? { notContain } : {}),
  };
}

function buildTikTokMediaFilters({
  mediaType = [],
  ...filters
}: CompetitivePostsScopedFilters): Partial<MediaFilters> {
  const mediaTypes = mediaType.map((value) => value.toUpperCase()).filter(isTikTokMediaType);
  const captionKeywords = buildCaptionKeywords(filters);
  if (mediaTypes.length === 0 && !captionKeywords) return {};

  return {
    tiktok: {
      ...(mediaTypes.length > 0 ? { mediaType: mediaTypes } : {}),
      ...(captionKeywords ? { captionKeywords } : {}),
    },
  };
}

export function buildTikTokSourceFilters(
  filters: CompetitivePostsScopedFilters,
): Partial<MediaFilters> {
  return {
    ...buildContentTagFilters(filters),
    ...buildHashtagFilters(filters),
    ...buildTikTokMediaFilters(filters),
  };
}
